'use client'

import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { SPState } from './useSPVideoPlayback'

const SWIPE_THRESHOLD = 50

// Animation-related reducer
type AnimationState = {
  slideAnimation: 'up' | 'down' | 'left' | null
  isDetailClosing: boolean
}

type AnimationAction =
  | { type: 'SET_SLIDE'; payload: 'up' | 'down' | 'left' | null }
  | { type: 'START_DETAIL_CLOSING' }
  | { type: 'FINISH_DETAIL_CLOSING' }

const animationInitialState: AnimationState = {
  slideAnimation: null,
  isDetailClosing: false,
}

function animationReducer(state: AnimationState, action: AnimationAction): AnimationState {
  switch (action.type) {
    case 'SET_SLIDE':
      return { ...state, slideAnimation: action.payload }
    case 'START_DETAIL_CLOSING':
      return { ...state, isDetailClosing: true }
    case 'FINISH_DETAIL_CLOSING':
      return { ...state, isDetailClosing: false }
    default:
      return state
  }
}

export interface UseSPSwipeNavigationParams {
  state: SPState
  showCarousel: boolean
  currentVideoIndex: number
  hasPrevious: boolean
  hasNext: boolean
  videoRef: RefObject<HTMLVideoElement | null>
  videoContainerRef: RefObject<HTMLDivElement | null>
  shouldAutoPlayRef: RefObject<boolean>
  onVideoSelect: (index: number) => void
  onPreviousVideo: () => void
  onNextVideo: () => void
  setState: (state: SPState) => void
  setShowControls: (show: boolean) => void
}

export interface UseSPSwipeNavigationReturn {
  animState: AnimationState
  animDispatch: React.Dispatch<AnimationAction>
  viewingHistory: number[]
  handleTouchStart: (e: React.TouchEvent) => void
  handleTouchEnd: (e: React.TouchEvent) => void
  navigateToVideoWithHistory: (targetIndex: number) => void
  handleGoBack: () => void
  handlePreviousWithAutoPlay: () => void
  handleNextWithAutoPlay: () => void
  handleShowDetail: () => void
  handleCloseDetail: () => void
}

export function useSPSwipeNavigation({
  state,
  showCarousel,
  currentVideoIndex,
  hasPrevious,
  hasNext,
  videoRef,
  videoContainerRef,
  shouldAutoPlayRef,
  onVideoSelect,
  onPreviousVideo,
  onNextVideo,
  setState,
  setShowControls,
}: UseSPSwipeNavigationParams): UseSPSwipeNavigationReturn {
  const [animState, animDispatch] = React.useReducer(animationReducer, animationInitialState)
  const [viewingHistory, setViewingHistory] = useState<number[]>([])
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)

  // Handle show detail - video continues playing
  const handleShowDetail = useCallback(() => {
    setState('detail')
    animDispatch({ type: 'FINISH_DETAIL_CLOSING' })
  }, [setState])

  // Handle close detail with animation
  const handleCloseDetail = useCallback(() => {
    animDispatch({ type: 'START_DETAIL_CLOSING' })
    setTimeout(() => {
      const video = videoRef.current
      if (video && !video.paused) {
        setState('playing')
        setShowControls(false)
      } else {
        setState('paused')
        setShowControls(true)
      }
      animDispatch({ type: 'FINISH_DETAIL_CLOSING' })
    }, 300)
  }, [videoRef, setState, setShowControls])

  // Previous video with auto-play
  const handlePreviousWithAutoPlay = useCallback(() => {
    shouldAutoPlayRef.current = true
    onPreviousVideo()
  }, [onPreviousVideo, shouldAutoPlayRef])

  // Next video with auto-play
  const handleNextWithAutoPlay = useCallback(() => {
    shouldAutoPlayRef.current = true
    onNextVideo()
  }, [onNextVideo, shouldAutoPlayRef])

  // A5: Navigate to video with history tracking
  const navigateToVideoWithHistory = useCallback(
    (targetIndex: number) => {
      setViewingHistory((prev) => [...prev, currentVideoIndex])
      shouldAutoPlayRef.current = true
      onVideoSelect(targetIndex)
    },
    [currentVideoIndex, onVideoSelect, shouldAutoPlayRef],
  )

  // A5: Go back to previous video in history
  const handleGoBack = useCallback(() => {
    if (viewingHistory.length === 0) return
    const prevIndex = viewingHistory[viewingHistory.length - 1]
    setViewingHistory((prev) => prev.slice(0, -1))
    shouldAutoPlayRef.current = true
    onVideoSelect(prevIndex)
  }, [viewingHistory, onVideoSelect, shouldAutoPlayRef])

  // A6: Swipe handlers for vertical (next/prev) and horizontal (back) navigation
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // Don't interfere with detail mode or carousel
      if (state === 'detail' || showCarousel) return
      const touch = e.touches[0]
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() }
    },
    [state, showCarousel],
  )

  // Register touchmove with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = videoContainerRef.current
    if (!el) return

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartRef.current) {
        const touch = e.touches[0]
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y)
        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x)
        if (deltaY > 10 || deltaX > 10) {
          e.preventDefault()
        }
      }
    }

    el.addEventListener('touchmove', handleTouchMove, { passive: false })
    return () => el.removeEventListener('touchmove', handleTouchMove)
  }, [videoContainerRef])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return
      const touch = e.changedTouches[0]
      const deltaX = touch.clientX - touchStartRef.current.x
      const deltaY = touch.clientY - touchStartRef.current.y
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)
      touchStartRef.current = null

      // Determine if swipe is primarily horizontal or vertical
      if (absDeltaX > absDeltaY && absDeltaX > SWIPE_THRESHOLD) {
        // Horizontal swipe: right swipe (positive deltaX) = go back
        if (deltaX > 0 && viewingHistory.length > 0) {
          animDispatch({ type: 'SET_SLIDE', payload: 'left' })
          setTimeout(() => {
            handleGoBack()
            animDispatch({ type: 'SET_SLIDE', payload: null })
          }, 200)
        }
      } else if (absDeltaY > absDeltaX && absDeltaY > SWIPE_THRESHOLD) {
        // Vertical swipe: up = next, down = previous
        if (deltaY < 0 && hasNext) {
          // Swipe up → next video
          animDispatch({ type: 'SET_SLIDE', payload: 'up' })
          setTimeout(() => {
            navigateToVideoWithHistory(currentVideoIndex + 1)
            animDispatch({ type: 'SET_SLIDE', payload: null })
          }, 200)
        } else if (deltaY > 0 && hasPrevious) {
          // Swipe down → previous video
          animDispatch({ type: 'SET_SLIDE', payload: 'down' })
          setTimeout(() => {
            navigateToVideoWithHistory(currentVideoIndex - 1)
            animDispatch({ type: 'SET_SLIDE', payload: null })
          }, 200)
        }
      }
    },
    [viewingHistory, hasNext, hasPrevious, currentVideoIndex, handleGoBack, navigateToVideoWithHistory],
  )

  return {
    animState,
    animDispatch,
    viewingHistory,
    handleTouchStart,
    handleTouchEnd,
    navigateToVideoWithHistory,
    handleGoBack,
    handlePreviousWithAutoPlay,
    handleNextWithAutoPlay,
    handleShowDetail,
    handleCloseDetail,
  }
}
